// Real Supabase Auth Service
// Replaces mock authentication with Supabase auth + database integration

import { Injectable, signal, computed, inject, NgZone } from '@angular/core';
import { Router } from '@angular/router';
import { Observable, BehaviorSubject, from, of, EMPTY } from 'rxjs';
import { catchError, map, switchMap, tap } from 'rxjs/operators';
import { getSupabaseClient } from '../supabase/supabase.client';
import { environment } from '../../../environments/environment';
import { User, UserRole, ClientProfile, CleanerProfile } from '../../models';
import type { AuthChangeEvent, Session } from '@supabase/supabase-js';
import { CleanerService } from '../../shared/services/cleaner.service';
import { LanguageService } from '../../i18n/language.service';
import { ClarityService } from '../services/clarity.service';
import { MetaPixelService } from '../analytics/meta-pixel.service';
import { PushDeviceService } from '../../shared/services/push-device.service';
import { Capacitor } from '@capacitor/core';
import { STORAGE_KEYS } from '../constants';

export interface MfaAssuranceLevel {
  currentLevel: string | null;
  nextLevel: string | null;
  currentAuthenticationMethods: Array<{ method?: string; timestamp?: number } | string>;
}

export interface MfaTotpFactor {
  id: string;
  friendlyName: string | null;
  status: string;
  createdAt?: string;
  updatedAt?: string;
}

export interface MfaSetupDetails {
  factorId: string;
  qrCode: string;
  secret: string;
  uri: string;
  friendlyName: string | null;
}

export type SignInResult =
  | { success: true; requiresMfa: false }
  | { success: true; requiresMfa: false; requiresMfaEnrollment: true }
  | { success: true; requiresMfa: true; factorId: string }
  | { success: false; error: string; accountDisabled?: boolean };

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  private supabase = getSupabaseClient();
  private router: Router;
  private ngZone = inject(NgZone);
  private cleanerService = inject(CleanerService);
  private languageService = inject(LanguageService);
  private clarityService = inject(ClarityService);
  private metaPixelService = inject(MetaPixelService);
  private pushDeviceService = inject(PushDeviceService);

  private currentUserSignal = signal<User | null>(null);
  private clientProfileSignal = signal<ClientProfile | null>(null);
  private cleanerProfileSignal = signal<CleanerProfile | null>(null);
  private sessionSubject = new BehaviorSubject<Session | null>(null);
  /** Mirrors sessionSubject so computed() signals invalidate when the session changes (BehaviorSubject alone does not). */
  private sessionSignal = signal<Session | null>(null);
  private initializedSignal = signal<boolean>(false);
  private initializationPromise: Promise<void> | null = null;

  readonly currentUser = this.currentUserSignal.asReadonly();
  readonly clientProfile = this.clientProfileSignal.asReadonly();
  readonly cleanerProfile = this.cleanerProfileSignal.asReadonly();
  /** True when we have a session or loaded user (so guards allow access right after signUp before DB trigger has run). */
  readonly isAuthenticated = computed(() => this.currentUserSignal() !== null || this.sessionSignal() !== null);
  /** Role from public.users when loaded; metadata fallback is client/cleaner only (never admin). */
  readonly userRole = computed(() => {
    const fromDb = this.currentUserSignal()?.role;
    if (fromDb) return fromDb;
    const metaRole = this.sessionSignal()?.user?.user_metadata?.['role'];
    if (metaRole === 'client' || metaRole === 'cleaner') return metaRole;
    return null;
  });
  readonly session$ = this.sessionSubject.asObservable();
  readonly initialized = this.initializedSignal.asReadonly();

  constructor(router: Router) {
    this.router = router;
    this.initializationPromise = this.initializeAuth();
  }

  private setSessionState(session: Session | null): void {
    this.sessionSubject.next(session);
    this.sessionSignal.set(session);
  }

  /**
   * Wait for auth initialization to complete
   * This should be called by guards before checking authentication status
   */
  async waitForInitialization(): Promise<void> {
    if (this.initializationPromise) {
      await this.initializationPromise;
    }
  }

  /**
   * JWT is valid-shaped but Auth no longer has this session row (revoked, DB reset, env switch).
   * Used to recover via refresh or clear stale client storage.
   */
  isSessionNotFoundError(error: { code?: string; message?: string } | null | undefined): boolean {
    if (!error) return false;
    const msg = error.message ?? '';
    return (
      error.code === 'session_not_found' ||
      msg.includes('session_id claim') ||
      msg.includes('Session from session_id')
    );
  }

  /**
   * When we have a session but currentUser/profile not yet loaded (e.g. right after signUp),
   * load user data and wait for it. Used by guards so navigation to onboarding/dashboard succeeds.
   */
  async ensureUserDataLoaded(): Promise<void> {
    const session = this.sessionSignal();
    if (!session?.user) return;
    if (this.currentUserSignal()) return;
    const userId = session.user.id;
    // Wait for Supabase client to have the session (setSession may still be in progress after signUp).
    for (let i = 0; i < 10; i++) {
      const { data: { session: clientSession } } = await this.supabase.auth.getSession();
      if (clientSession?.user?.id === userId) break;
      await new Promise(r => setTimeout(r, 300));
    }
    const maxWaitMs = 8000;
    const stepMs = 400;
    const deadline = Date.now() + maxWaitMs;
    while (Date.now() < deadline) {
      const loadState = await this.loadUserData(userId);
      if (loadState === 'inactive') {
        return;
      }
      if (this.currentUserSignal()) return;
      await new Promise(r => setTimeout(r, stepMs));
    }
  }

  /**
   * Initialize auth state - restore session and listen for changes.
   * The onAuthStateChange callback is fired by Supabase outside Angular's
   * zone (it uses its own timers / WebSocket), so every state mutation
   * must be wrapped in ngZone.run() to keep change detection alive.
   */
  private async initializeAuth(): Promise<void> {
    try {
      const { data: { session }, error } = await this.supabase.auth.getSession();
      
      if (error) {
        console.error('Error getting session:', error);
        this.initializedSignal.set(true);
        return;
      }

      if (session) {
        this.setSessionState(session);
        await this.loadUserData(session.user.id);
      }

      this.supabase.auth.onAuthStateChange((event, session) => {
        this.ngZone.run(async () => {
          try {
            this.setSessionState(session);
            if (session?.user) {
              await this.loadUserData(session.user.id);
            } else {
              this.clearUserData();
            }
          } catch (err) {
            console.error('Error in auth state change handler:', err);
            this.clearUserData();
          }
        });
      });

      this.initializedSignal.set(true);
    } catch (error) {
      console.error('Error initializing auth:', error);
      this.initializedSignal.set(true);
    }
  }

  /**
   * Load user data from public.users and profiles.
   * Retries when user row is not found (handle_new_user trigger can be slightly delayed on first login).
   */
  private async loadUserData(userId: string): Promise<'ok' | 'inactive' | 'error'> {
    const maxRetries = 4;
    const retryDelayMs = 350;
    let lastError: unknown = null;

    for (let attempt = 1; attempt <= maxRetries; attempt++) {
      try {
        const { data: user, error } = await this.supabase
          .from('users')
          .select('*')
          .eq('id', userId)
          .single();

        if (error) {
          lastError = error;
          const notFound = error.code === 'PGRST116' || (error as any).message?.includes('not found');
          if (notFound && attempt < maxRetries) {
            await new Promise(resolve => setTimeout(resolve, retryDelayMs));
            continue;
          }
          console.error('Error loading user:', error);
          return 'error';
        }

        if (!user) {
          lastError = new Error('User not found in public.users');
          if (attempt < maxRetries) {
            await new Promise(resolve => setTimeout(resolve, retryDelayMs));
            continue;
          }
          console.error('User not found in public.users');
          return 'error';
        }

        // Map database row to User model
        const mappedUser: User = {
          id: (user as any).id,
          email: (user as any).email,
          role: (user as any).role as UserRole,
          preferredLanguage: (user as any).preferred_language || undefined,
          firstName: (user as any).first_name,
          lastName: (user as any).last_name,
          phone: (user as any).phone || undefined,
          avatarUrl: (user as any).avatar_url || undefined,
          isActive: (user as any).is_active,
          emailVerified: (user as any).email_verified,
          lastLoginAt: (user as any).last_login_at ? new Date((user as any).last_login_at) : undefined,
          stripeCustomerId: (user as any).stripe_customer_id || undefined,
          stripeAccountId: (user as any).stripe_account_id || undefined,
          createdAt: new Date((user as any).created_at),
          updatedAt: new Date((user as any).updated_at),
        };

        if (mappedUser.isActive === false) {
          await this.signOutAccountDisabled();
          return 'inactive';
        }

        this.currentUserSignal.set(mappedUser);
        this.clarityService.identifyUser(mappedUser);
        this.clarityService.setUserTags(mappedUser);
        this.clarityService.markLoggedIn();
        if (mappedUser.preferredLanguage && this.languageService.currentLanguage() !== mappedUser.preferredLanguage) {
          this.languageService.setLanguage(mappedUser.preferredLanguage);
        }

        // Load role-specific profile
        await this.loadProfiles(mappedUser);
        void this.pushDeviceService.syncAfterLogin(mappedUser.id);
        return 'ok';
      } catch (error) {
        if (attempt === maxRetries) {
          console.error('Error loading user data:', error);
        }
        lastError = error;
        if (attempt < maxRetries) {
          await new Promise(resolve => setTimeout(resolve, retryDelayMs));
        }
      }
    }
    return 'error';
  }

  /**
   * Load role-specific profile data
   */
  private async loadProfiles(user: User): Promise<void> {
    try {
      if (user.role === 'client') {
        const { data, error } = await this.supabase
          .from('client_profiles')
          .select('*')
          .eq('user_id', user.id)
          .maybeSingle(); // Use maybeSingle instead of single to avoid PGRST116

        if (error && error.code !== 'PGRST116') {
          console.error('Error loading client profile:', error);
          return;
        }

        // If profile doesn't exist, create it (fallback for users created before migration)
        if (!data) {
          console.log('⚠️ Client profile not found, creating via RPC (fallback)...');
          try {
            await this.createClientProfile(user.id, false); // Default to B2C
            // Retry loading after creation
            const { data: retryData, error: retryError } = await this.supabase
              .from('client_profiles')
              .select('*')
              .eq('user_id', user.id)
              .maybeSingle();
            
            if (retryError || !retryData) {
              console.error('Error loading client profile after creation:', retryError);
              return;
            }
            
            const d = retryData as any;
            const profile: ClientProfile = {
              userId: d.user_id,
              companyName: d.company_name || undefined,
              vatNumber: d.vat_number || undefined,
              isB2B: d.is_b2b,
              billingAddress: d.billing_address as any,
              defaultServiceAddress: d.default_service_address as any,
              preferredLanguage: d.preferred_language,
              paymentMethodId: d.payment_method_id || undefined,
              createdAt: new Date(d.created_at),
              updatedAt: new Date(d.updated_at),
            };
            this.clientProfileSignal.set(profile);
            if (this.languageService.currentLanguage() !== profile.preferredLanguage) {
              this.languageService.setLanguage(profile.preferredLanguage);
            }
            return;
          } catch (createError) {
            console.error('Error creating missing client profile:', createError);
            return;
          }
        }

        // Profile exists, map it
        const d = data as any;
        const profile: ClientProfile = {
          userId: d.user_id,
          companyName: d.company_name || undefined,
          vatNumber: d.vat_number || undefined,
          isB2B: d.is_b2b,
          billingAddress: d.billing_address as any,
          defaultServiceAddress: d.default_service_address as any,
          preferredLanguage: d.preferred_language,
          paymentMethodId: d.payment_method_id || undefined,
          createdAt: new Date(d.created_at),
          updatedAt: new Date(d.updated_at),
        };
        this.clientProfileSignal.set(profile);
        if (this.languageService.currentLanguage() !== profile.preferredLanguage) {
          this.languageService.setLanguage(profile.preferredLanguage);
        }
      } else if (user.role === 'cleaner') {
        const { data, error } = await this.supabase
          .from('cleaner_profiles')
          .select('*')
          .eq('user_id', user.id)
          .maybeSingle(); // Use maybeSingle instead of single to avoid PGRST116

        if (error && error.code !== 'PGRST116') {
          console.error('Error loading cleaner profile:', error);
          return;
        }

        // If profile doesn't exist, create it (fallback for users created before migration)
        if (!data) {
          console.log('⚠️ Cleaner profile not found, creating via RPC (fallback)...');
          try {
            await this.createCleanerProfile(user.id);
            // Retry loading after creation
            const { data: retryData, error: retryError } = await this.supabase
              .from('cleaner_profiles')
              .select('*')
              .eq('user_id', user.id)
              .maybeSingle();
            
            if (retryError || !retryData) {
              console.error('Error loading cleaner profile after creation:', retryError);
              return;
            }
            
            const d = retryData as any;
            const profile: CleanerProfile = {
              userId: d.user_id,
              bio: d.bio || undefined,
              spokenLanguages: d.spoken_languages as any,
              hourlyRateB2C: d.hourly_rate_b2c,
              fixedRateB2B: d.fixed_rate_b2b || undefined,
              isAvailable: d.is_available,
              profileVisibility: d.profile_visibility,
              approvalStatus: d.approval_status,
              approvedAt: d.approved_at ? new Date(d.approved_at) : undefined,
              approvedBy: d.approved_by || undefined,
              rejectionReason: d.rejection_reason || undefined,
              baseLocation: d.base_location as any,
              maxTravelDistance: d.max_travel_distance,
              kmCompensation: d.km_compensation || undefined,
              kmCompensationIncluded: d.km_compensation_included,
              serviceAreas: d.service_areas,
              serviceCategories: d.service_categories as any,
              serviceSubcategories: d.service_subcategories as any,
              rating: d.rating || undefined,
              reviewCount: d.review_count,
              totalJobsCompleted: d.total_jobs_completed,
              commissionTier: d.commission_tier,
              iban: d.iban || undefined,
              accountHolderName: d.account_holder_name || undefined,
              vatNumber: d.vat_number || undefined,
              businessName: d.business_name || undefined,
              businessAddress: d.business_address as any,
              insuranceExpiryDate: d.insurance_expiry_date ? new Date(d.insurance_expiry_date) : undefined,
              insuranceVerified: d.insurance_verified,
              stripeAccountId: d.stripe_account_id || undefined,
              stripeAccountStatus: d.stripe_account_status || undefined,
              onboardingProgress: d.onboarding_progress as any,
              documents: [], // Documents loaded separately
              createdAt: new Date(d.created_at),
              updatedAt: new Date(d.updated_at),
            };
            this.cleanerProfileSignal.set(profile);
            return;
          } catch (createError) {
            console.error('Error creating missing cleaner profile:', createError);
            return;
          }
        }

        // Profile exists, map it
        const d = data as any;
        const profile: CleanerProfile = {
          userId: d.user_id,
          bio: d.bio || undefined,
          spokenLanguages: d.spoken_languages as any,
          hourlyRateB2C: d.hourly_rate_b2c,
          fixedRateB2B: d.fixed_rate_b2b || undefined,
          isAvailable: d.is_available,
          profileVisibility: d.profile_visibility,
          approvalStatus: d.approval_status,
          approvedAt: d.approved_at ? new Date(d.approved_at) : undefined,
          approvedBy: d.approved_by || undefined,
          rejectionReason: d.rejection_reason || undefined,
          baseLocation: d.base_location as any,
          maxTravelDistance: d.max_travel_distance,
          kmCompensation: d.km_compensation || undefined,
          kmCompensationIncluded: d.km_compensation_included,
          serviceAreas: d.service_areas,
          serviceCategories: d.service_categories as any,
          serviceSubcategories: d.service_subcategories as any,
          rating: d.rating || undefined,
          reviewCount: d.review_count,
          totalJobsCompleted: d.total_jobs_completed,
          commissionTier: d.commission_tier,
          iban: d.iban || undefined,
          accountHolderName: d.account_holder_name || undefined,
          vatNumber: d.vat_number || undefined,
          businessName: d.business_name || undefined,
          businessAddress: d.business_address as any,
          insuranceExpiryDate: d.insurance_expiry_date ? new Date(d.insurance_expiry_date) : undefined,
          insuranceVerified: d.insurance_verified,
          stripeAccountId: d.stripe_account_id || undefined,
          stripeAccountStatus: d.stripe_account_status || undefined,
          onboardingProgress: d.onboarding_progress as any,
          documents: [], // Documents loaded separately
          createdAt: new Date(d.created_at),
          updatedAt: new Date(d.updated_at),
        };
        this.cleanerProfileSignal.set(profile);
      }
    } catch (error) {
      console.error('Error loading profiles:', error);
    }
  }

  /**
   * Clear user data on logout (client, cleaner, admin).
   * Clears in-memory state and session observable so no subscriber sees stale data.
   */
  private clearUserData(): void {
    this.pushDeviceService.clearUser();
    this.clarityService.markLoggedOut();
    this.setSessionState(null);
    this.currentUserSignal.set(null);
    this.clientProfileSignal.set(null);
    this.cleanerProfileSignal.set(null);
  }

  /** Single CompleteRegistration per auth user id in this browsing session (guards double-fire). */
  private trackMetaPixelRegistrationOnce(userId: string): void {
    if (typeof sessionStorage !== 'undefined') {
      const key = `fbq_complete_registration_${userId}`;
      if (sessionStorage.getItem(key)) {
        return;
      }
      sessionStorage.setItem(key, '1');
    }
    this.metaPixelService.trackCompleteRegistration();
  }

  /**
   * Sign up a new user
   */
  async signUp(
    email: string,
    password: string,
    firstName: string,
    lastName: string,
    role: 'client' | 'cleaner',
    additionalData?: { isB2B?: boolean; phone?: string }
  ): Promise<{ success: boolean; error?: string; requiresEmailConfirmation?: boolean }> {
    try {
      const authRedirectUrl = this.getAuthRedirectUrl('/auth/callback');
      const { data, error } = await this.supabase.auth.signUp({
        email,
        password,
        options: {
          emailRedirectTo: authRedirectUrl,
          data: {
            first_name: firstName,
            last_name: lastName,
            role,
            phone: additionalData?.phone,
            is_b2b: additionalData?.isB2B || false,
            preferred_language: this.languageService.currentLanguage(),
          },
        },
      });

      if (error) {
        return { success: false, error: error.message };
      }

      if (!data.user) {
        return { success: false, error: 'Failed to create user' };
      }

      // public.users row and profile are created by handle_new_user trigger (or on first login when confirmation is required)
      if (data.session) {
        // Push session to our state immediately so isAuthenticated is true and guard allows navigation.
        // setSession is not awaited: it can block when onAuthStateChange runs loadUserData and that blocks the promise chain.
        this.setSessionState(data.session);
        this.supabase.auth.setSession({
          access_token: data.session.access_token,
          refresh_token: data.session.refresh_token
        }).catch((err) => console.error('[Auth] setSession failed', err));
        void this.waitForUserAndProfileThenLoad(data.user.id, role, additionalData?.isB2B || false);
      } else {
        // Email confirmation required: no session yet. Trigger runs on signup; profile is ensured on first login via handle_email_verification.
        console.log('ℹ️ No session (email confirmation required). Profile will be created by trigger on first login.');
      }

      this.trackMetaPixelRegistrationOnce(data.user.id);

      return { success: true, requiresEmailConfirmation: !data.session };
    } catch (error: any) {
      return { success: false, error: error.message || 'Sign up failed' };
    }
  }

  /**
   * When we have a session right after signUp (no email confirmation): wait for handle_new_user
   * trigger to create public.users and profile, create profile via RPC if missing, then load user data.
   * Works even if the trigger is slightly delayed.
   */
  private async waitForUserAndProfileThenLoad(userId: string, role: 'client' | 'cleaner', isB2B: boolean): Promise<void> {
    const maxAttempts = 15;
    const delayMs = 400;

    for (let attempt = 1; attempt <= maxAttempts; attempt++) {
      const { data: userRow } = await this.supabase.from('users').select('id').eq('id', userId).maybeSingle();
      if (userRow) {
        await this.ensureProfileExists(userId, role, isB2B);
        await this.loadUserData(userId);
        return;
      }
      if (attempt < maxAttempts) {
        await new Promise(resolve => setTimeout(resolve, delayMs));
      }
    }
    await this.ensureProfileExists(userId, role, isB2B);
    await this.loadUserData(userId);
  }

  /**
   * Ensure profile exists - check and create if missing (fallback safety check)
   * The database trigger should create profiles automatically, but this is a safety net
   */
  private async ensureProfileExists(userId: string, role: 'client' | 'cleaner', isB2B: boolean = false): Promise<void> {
    try {
      if (role === 'client') {
        // Check if client profile exists
        const { data, error } = await this.supabase
          .from('client_profiles')
          .select('user_id')
          .eq('user_id', userId)
          .maybeSingle();

        if (error && error.code !== 'PGRST116') {
          console.error('Error checking client profile:', error);
          return;
        }

        // Create profile if it doesn't exist
        if (!data) {
          console.log('⚠️ Client profile missing, creating via RPC (fallback)...');
          await this.createClientProfile(userId, isB2B);
        }
      } else if (role === 'cleaner') {
        // Check if cleaner profile exists
        const { data, error } = await this.supabase
          .from('cleaner_profiles')
          .select('user_id')
          .eq('user_id', userId)
          .maybeSingle();

        if (error && error.code !== 'PGRST116') {
          console.error('Error checking cleaner profile:', error);
          return;
        }

        // Create profile if it doesn't exist
        if (!data) {
          console.log('⚠️ Cleaner profile missing, creating via RPC (fallback)...');
          await this.createCleanerProfile(userId);
        }
      }
    } catch (error) {
      console.error('Error ensuring profile exists:', error);
      // Don't throw - profile creation is handled by trigger
    }
  }

  /**
   * Create client profile using RPC function (bypasses RLS)
   * Used as fallback if trigger didn't create profile
   */
  private async createClientProfile(userId: string, isB2B: boolean): Promise<void> {
    const defaultAddress = {
      street: '',
      houseNumber: '',
      city: '',
      postalCode: '',
      country: 'Belgium',
    };

    console.log('🔐 Creating client profile via RPC for userId:', userId);

    // Type assertion needed because RPC function types aren't fully inferred
    const { error } = await (this.supabase.rpc as any)('create_client_profile', {
      p_user_id: userId,
      p_is_b2b: isB2B,
      p_preferred_language: this.languageService.currentLanguage(),
      p_billing_address: defaultAddress,
    });

    if (error) {
      console.error('Error creating client profile via RPC:', error);
      throw error;
    }
    
    console.log('✅ Client profile created successfully');
  }

  /**
   * Create cleaner profile using RPC function (bypasses RLS)
   * Used as fallback if trigger didn't create profile
   */
  private async createCleanerProfile(userId: string): Promise<void> {
    console.log('🔐 Creating cleaner profile via RPC for userId:', userId);

    // Type assertion needed because RPC function types aren't fully inferred
    const { error } = await (this.supabase.rpc as any)('create_cleaner_profile', {
      p_user_id: userId,
    });

    if (error) {
      console.error('Error creating cleaner profile via RPC:', error);
      throw error;
    }
    
    console.log('✅ Cleaner profile created successfully');
  }

  /**
   * Sign in with email and password
   */
  async signIn(email: string, password: string): Promise<SignInResult> {
    try {
      const { data, error } = await this.supabase.auth.signInWithPassword({
        email,
        password,
      });

      if (error) {
        return { success: false, error: error.message };
      }

      if (!data.session?.user) {
        return { success: false, error: 'No session created' };
      }

      // Keep session immediately; route access is still enforced by guards.
      this.setSessionState(data.session);

      const loadResult = await this.loadUserData(data.session.user.id);
      if (loadResult === 'inactive') {
        return { success: false, error: 'auth.accountDisabled', accountDisabled: true };
      }
      if (loadResult === 'error') {
        await this.clearAuthSessionSilently();
        return { success: false, error: 'auth.profileLoadFailed' };
      }
      return { success: true, requiresMfa: false };
    } catch (error: any) {
      return { success: false, error: error.message || 'Sign in failed' };
    }
  }

  getAuthenticatedUserId(): string | null {
    return this.currentUserSignal()?.id ?? this.sessionSignal()?.user?.id ?? null;
  }

  /**
   * After password sign-in, whether the app must send email OTP before treating the user as logged in.
   * Defaults to true if the profile row is missing or unreadable (fail closed).
   */
  async getRequireEmailLoginOtpForUser(userId: string): Promise<boolean> {
    try {
      const role = await this.resolveRoleForUserId(userId);

      if (role === 'cleaner' && (await this.hasValidEmailLoginTrustedDevice(userId))) {
        return false;
      }

      const { data, error } = await this.supabase
        .from('users')
        .select('require_email_login_otp')
        .eq('id', userId)
        .maybeSingle();

      if (error || data == null) {
        return true;
      }
      return data.require_email_login_otp !== false;
    } catch {
      return true;
    }
  }

  async sendEmailLoginLink(email: string): Promise<{ success: boolean; error?: string }> {
    try {
      const redirectTo = this.getAuthRedirectUrl('/auth/callback');
      await this.signOut();

      const { error } = await this.supabase.auth.signInWithOtp({
        email,
        options: { emailRedirectTo: redirectTo },
      });

      if (error) {
        return { success: false, error: error.message };
      }

      return { success: true };
    } catch (error: any) {
      return { success: false, error: error?.message || 'Failed to send login link' };
    }
  }

  async sendEmailOtpCode(email: string): Promise<{ success: boolean; error?: string }> {
    try {
      await this.clearAuthSessionSilently();

      const { error } = await this.supabase.auth.signInWithOtp({
        email,
        options: {
          shouldCreateUser: false,
        },
      });

      if (error) {
        return { success: false, error: error.message };
      }
      return { success: true };
    } catch (error: any) {
      return { success: false, error: error?.message || 'Failed to send login code' };
    }
  }

  async verifyEmailOtpCode(
    email: string,
    token: string,
    rememberDevice = false
  ): Promise<{ success: boolean; error?: string; accountDisabled?: boolean }> {
    try {
      const { data, error } = await this.supabase.auth.verifyOtp({
        email,
        token,
        type: 'email',
      });

      if (error) {
        return { success: false, error: error.message };
      }

      if (!data.session?.user) {
        return { success: false, error: 'No session created' };
      }

      this.setSessionState(data.session);
      const otpLoad = await this.loadUserData(data.session.user.id);
      if (otpLoad === 'inactive') {
        return { success: false, error: 'auth.accountDisabled', accountDisabled: true };
      }
      if (otpLoad === 'error') {
        await this.clearAuthSessionSilently();
        return { success: false, error: 'auth.profileLoadFailed' };
      }

      if (rememberDevice && this.userRole() === 'cleaner') {
        await this.issueEmailLoginTrustedDevice();
      }

      return { success: true };
    } catch (error: any) {
      return { success: false, error: error?.message || 'OTP verification failed' };
    }
  }

  async getMfaAssuranceLevel(): Promise<MfaAssuranceLevel> {
    const { data, error } = await this.supabase.auth.mfa.getAuthenticatorAssuranceLevel();
    if (error) {
      throw error;
    }

    return {
      currentLevel: data?.currentLevel ?? null,
      nextLevel: data?.nextLevel ?? null,
      currentAuthenticationMethods: data?.currentAuthenticationMethods ?? [],
    };
  }

  async requiresMfaVerification(): Promise<boolean> {
    if (!this.isMfaMandatoryEnvironment()) return false;

    const session = this.sessionSignal();
    if (!session?.user) return false;

    const assurance = await this.getMfaAssuranceLevel();
    return assurance.currentLevel !== 'aal2' && assurance.nextLevel === 'aal2';
  }

  isMfaMandatoryEnvironment(): boolean {
    return false;
  }

  private async resolveRoleForUserId(userId: string): Promise<UserRole | null> {
    if (this.getAuthenticatedUserId() === userId) {
      const role = this.userRole();
      if (role) return role;
    }

    const { data, error } = await this.supabase
      .from('users')
      .select('role')
      .eq('id', userId)
      .maybeSingle();

    if (error || !data?.role) {
      return null;
    }

    return data.role as UserRole;
  }

  private readEmailLoginTrustedToken(userId: string): string | null {
    if (typeof localStorage === 'undefined') return null;
    return localStorage.getItem(STORAGE_KEYS.emailLoginTrustedToken(userId));
  }

  private writeEmailLoginTrustedToken(userId: string, token: string): void {
    if (typeof localStorage === 'undefined') return;
    localStorage.setItem(STORAGE_KEYS.emailLoginTrustedToken(userId), token);
  }

  private clearEmailLoginTrustedToken(userId: string): void {
    if (typeof localStorage === 'undefined') return;
    localStorage.removeItem(STORAGE_KEYS.emailLoginTrustedToken(userId));
  }

  async hasValidEmailLoginTrustedDevice(userId: string): Promise<boolean> {
    const token = this.readEmailLoginTrustedToken(userId);
    if (!token) return false;

    try {
      const { data, error } = await this.supabase.rpc('validate_email_login_trusted_device', {
        p_token: token,
      });

      if (error || data !== true) {
        this.clearEmailLoginTrustedToken(userId);
        return false;
      }

      return true;
    } catch {
      return false;
    }
  }

  async issueEmailLoginTrustedDevice(): Promise<void> {
    const userId = this.getAuthenticatedUserId();
    if (!userId || this.userRole() !== 'cleaner') return;

    try {
      const { data, error } = await this.supabase.rpc('issue_email_login_trusted_device');

      if (error || typeof data !== 'string' || !data) {
        console.warn('Failed to issue email login trusted device:', error?.message);
        return;
      }

      this.writeEmailLoginTrustedToken(userId, data);
    } catch (error) {
      console.warn('Failed to issue email login trusted device:', error);
    }
  }

  async revokeAllEmailLoginTrustedDevices(): Promise<void> {
    try {
      await this.supabase.rpc('revoke_all_email_login_trusted_devices');
    } catch {
      // Best effort.
    }

    const userId = this.getAuthenticatedUserId();
    if (userId) {
      this.clearEmailLoginTrustedToken(userId);
    }
  }

  async requiresMfaEnrollment(): Promise<boolean> {
    if (!this.isMfaMandatoryEnvironment()) return false;
    const session = this.sessionSignal();
    if (!session?.user) return false;

    const factors = await this.getVerifiedTotpFactors();
    return factors.length === 0;
  }

  async getVerifiedTotpFactors(): Promise<MfaTotpFactor[]> {
    const { data, error } = await this.supabase.auth.mfa.listFactors();
    if (error) {
      throw error;
    }

    const totp = data?.totp ?? [];
    return totp
      .filter((factor: any) => factor.status === 'verified')
      .map((factor: any) => ({
        id: factor.id,
        friendlyName: factor.friendly_name ?? null,
        status: factor.status,
        createdAt: factor.created_at,
        updatedAt: factor.updated_at,
      }));
  }

  async enrollTotpFactor(friendlyName?: string): Promise<MfaSetupDetails> {
    let { data, error } = await this.supabase.auth.mfa.enroll({
      factorType: 'totp',
      friendlyName,
    });

    if (error && friendlyName && /friendly name|already exists/i.test(error.message ?? '')) {
      await this.clearUnverifiedTotpFactorsByFriendlyName(friendlyName);

      ({ data, error } = await this.supabase.auth.mfa.enroll({
        factorType: 'totp',
        friendlyName,
      }));

      // Last fallback: let Supabase generate a label to avoid duplicate-name hard failure.
      if (error) {
        ({ data, error } = await this.supabase.auth.mfa.enroll({
          factorType: 'totp',
        }));
      }
    }

    if (error || !data?.id || !data?.totp?.secret || !data?.totp?.uri) {
      throw error ?? new Error('Failed to enroll MFA factor');
    }

    const qrCode = this.normalizeTotpQrCode(data.totp.qr_code);
    if (!qrCode) {
      throw new Error('No QR code received for MFA enrollment');
    }

    return {
      factorId: data.id,
      qrCode,
      secret: data.totp.secret,
      uri: data.totp.uri,
      friendlyName: data.friendly_name ?? null,
    };
  }

  private async clearUnverifiedTotpFactorsByFriendlyName(friendlyName: string): Promise<void> {
    const { data, error } = await this.supabase.auth.mfa.listFactors();
    if (error) return;

    const totpFactors = (data?.totp ?? []) as Array<{ id: string; status?: string; friendly_name?: string | null }>;
    const stale = totpFactors.filter((factor) =>
      factor.status !== 'verified' && (factor.friendly_name ?? null) === friendlyName
    );

    for (const factor of stale) {
      await this.supabase.auth.mfa.unenroll({ factorId: factor.id });
    }
  }

  async verifyTotpEnrollment(factorId: string, code: string): Promise<{ success: boolean; error?: string }> {
    const { error } = await this.supabase.auth.mfa.challengeAndVerify({
      factorId,
      code,
    });

    if (error) {
      return { success: false, error: error.message };
    }

    return { success: true };
  }

  async verifyMfaSignIn(factorId: string, code: string): Promise<{ success: boolean; error?: string }> {
    const { error } = await this.supabase.auth.mfa.challengeAndVerify({
      factorId,
      code,
    });

    if (error) {
      return { success: false, error: error.message };
    }

    const session = this.sessionSignal();
    if (session?.user) {
      const mfaLoad = await this.loadUserData(session.user.id);
      if (mfaLoad === 'inactive') {
        return { success: false, error: 'auth.accountDisabled' };
      }
      if (mfaLoad === 'error') {
        await this.clearAuthSessionSilently();
        return { success: false, error: 'auth.profileLoadFailed' };
      }
    }

    return { success: true };
  }

  async disableTotpFactor(factorId: string, code: string): Promise<{ success: boolean; error?: string }> {
    const verify = await this.verifyTotpEnrollment(factorId, code);
    if (!verify.success) {
      return verify;
    }

    const { error } = await this.supabase.auth.mfa.unenroll({ factorId });
    if (error) {
      return { success: false, error: error.message };
    }

    return { success: true };
  }

  private normalizeTotpQrCode(rawQrCode?: string | null): string | null {
    if (!rawQrCode) return null;
    if (rawQrCode.startsWith('data:image/')) {
      return rawQrCode;
    }
    if (rawQrCode.startsWith('<svg')) {
      return `data:image/svg+xml;utf8,${encodeURIComponent(rawQrCode)}`;
    }
    return rawQrCode;
  }

  /**
   * Sign out - clears session for client, cleaner, and admin.
   * Deactivates push tokens while the session is still valid (so DB RPC sees auth.uid()),
   * then clears local state (UI/guards unauthenticated), then signs out from Supabase.
   * Always clears and navigates even if server signOut fails (e.g. offline).
   */
  async signOut(): Promise<void> {
    await this.pushDeviceService.deactivateAllPushDevices();
    this.clearUserData();
    try {
      const { error } = await this.supabase.auth.signOut();
      if (error) {
        console.error('Error signing out:', error);
      }
    } catch (error) {
      console.error('Error signing out:', error);
    } finally {
      this.ngZone.run(() => {
        this.router.navigateByUrl('/login', { replaceUrl: true });
      });
    }
  }

  /**
   * Signs out and sends the user to login with a query flag (account disabled by admin).
   */
  async signOutAccountDisabled(): Promise<void> {
    await this.pushDeviceService.deactivateAllPushDevices();
    this.clearUserData();
    try {
      const { error } = await this.supabase.auth.signOut();
      if (error) {
        console.error('Error signing out:', error);
      }
    } catch (error) {
      console.error('Error signing out:', error);
    } finally {
      this.ngZone.run(() => {
        this.router.navigateByUrl('/login?accountDisabled=1', { replaceUrl: true });
      });
    }
  }

  /**
   * Re-checks public.users.is_active for the current session (e.g. admin just disabled the account).
   * Returns false if the user was signed out.
   */
  async enforceActiveAccountOrSignOut(): Promise<boolean> {
    const session = this.sessionSignal();
    if (!session?.user?.id) {
      return true;
    }
    const { data, error } = await this.supabase
      .from('users')
      .select('is_active')
      .eq('id', session.user.id)
      .single();
    if (error || !data) {
      return true;
    }
    if ((data as { is_active: boolean | null }).is_active === false) {
      await this.signOutAccountDisabled();
      return false;
    }
    return true;
  }

  /**
   * Logout (alias for signOut for backward compatibility)
   */
  async logout(): Promise<void> {
    return this.signOut();
  }

  /**
   * Register (alias for signUp for backward compatibility)
   */
  async register(
    email: string,
    password: string,
    firstName: string,
    lastName: string,
    role: 'client' | 'cleaner',
    isB2B: boolean = false
  ): Promise<{ success: boolean; error?: string; requiresEmailConfirmation?: boolean }> {
    return this.signUp(email, password, firstName, lastName, role, { isB2B });
  }

  /**
   * Reset password
   */
  async resetPassword(email: string): Promise<{ success: boolean; error?: string }> {
    try {
      const baseUrl = this.getAuthRedirectUrl('/reset-password');
      const { error } = await this.supabase.auth.resetPasswordForEmail(email, {
        redirectTo: baseUrl,
      });

      if (error) {
        return { success: false, error: error.message };
      }

      return { success: true };
    } catch (error: any) {
      return { success: false, error: error.message || 'Password reset failed' };
    }
  }

  async handleAuthRedirectUrl(url: string): Promise<void> {
    const details = this.parseAuthUrl(url);
    if (!details) return;

    if (details.accessToken && details.refreshToken) {
      const { data, error } = await this.supabase.auth.setSession({
        access_token: details.accessToken,
        refresh_token: details.refreshToken,
      });
      if (error) {
        console.error('Failed to restore session from auth redirect URL:', error);
      } else {
        this.setSessionState(data.session);
        if (data.session?.user) {
          await this.loadUserData(data.session.user.id);
        }
      }
    }

    if (details.type === 'recovery' && details.fragment) {
      this.ngZone.run(() => {
        this.router.navigate(['/reset-password'], { replaceUrl: true, fragment: details.fragment ?? undefined });
      });
      return;
    }

    const mustEnroll = await this.requiresMfaEnrollment().catch(() => false);
    if (mustEnroll) {
      this.ngZone.run(() => {
        this.router.navigate(['/settings'], { replaceUrl: true, queryParams: { mfa: 'setup' } });
      });
      return;
    }

    const mustVerify = await this.requiresMfaVerification().catch(() => false);
    if (mustVerify) {
      this.ngZone.run(() => {
        this.router.navigate(['/login'], { replaceUrl: true, queryParams: { mfa: 'required' } });
      });
      return;
    }

    const role = this.userRole();
    const target = role ? this.getRedirectUrlForRole(role) : '/login';
    this.ngZone.run(() => {
      this.router.navigate([target], { replaceUrl: true });
    });
  }

  private getAppBaseUrl(): string {
    return (environment as { appUrl?: string }).appUrl ?? window.location.origin;
  }

  private getAuthRedirectUrl(path: `/${string}`): string {
    const cleanPath = path.replace(/^\/+/, '');
    if (Capacitor.isNativePlatform()) {
      const scheme = (environment as { mobileAppScheme?: string }).mobileAppScheme;
      if (scheme) {
        return `${scheme}://${cleanPath}`;
      }
    }
    return `${this.getAppBaseUrl()}${path}`;
  }

  private parseAuthUrl(url: string): { type: string | null; accessToken: string | null; refreshToken: string | null; fragment: string | null } | null {
    const hashIndex = url.indexOf('#');
    const fragment = hashIndex >= 0 ? url.slice(hashIndex + 1) : '';
    const queryStart = url.indexOf('?');
    const queryEnd = hashIndex >= 0 ? hashIndex : undefined;
    const query = queryStart >= 0 ? url.slice(queryStart + 1, queryEnd) : '';

    const hashParams = new URLSearchParams(fragment);
    const queryParams = new URLSearchParams(query);

    const type = hashParams.get('type') ?? queryParams.get('type');
    const accessToken = hashParams.get('access_token') ?? queryParams.get('access_token');
    const refreshToken = hashParams.get('refresh_token') ?? queryParams.get('refresh_token');

    if (!type && !accessToken && !refreshToken) {
      return null;
    }

    return {
      type,
      accessToken,
      refreshToken,
      fragment: fragment || null,
    };
  }

  /**
   * Verify the current password without changing session state.
   */
  async verifyCurrentPassword(currentPassword: string): Promise<{ success: boolean; error?: string }> {
    const email = this.currentUserSignal()?.email;
    if (!email) {
      return { success: false, error: 'settings.passwordChangeNotAvailable' };
    }

    try {
      const { error } = await this.supabase.auth.signInWithPassword({
        email,
        password: currentPassword,
      });

      if (error) {
        const msg = error.message ?? '';
        if (/invalid login credentials/i.test(msg)) {
          return { success: false, error: 'settings.currentPasswordIncorrect' };
        }
        return { success: false, error: msg || 'settings.passwordChangeFailed' };
      }

      return { success: true };
    } catch (error: any) {
      const msg = error?.message ?? '';
      const isAborted = /abort|signal/i.test(msg);
      return {
        success: false,
        error: isAborted ? 'auth.connectionInterrupted' : (msg || 'settings.passwordChangeFailed'),
      };
    }
  }

  /**
   * Update password
   */
  async updatePassword(newPassword: string): Promise<{ success: boolean; error?: string }> {
    try {
      const { error } = await this.supabase.auth.updateUser({
        password: newPassword,
      });

      if (error) {
        return { success: false, error: error.message };
      }

      await this.revokeAllEmailLoginTrustedDevices();

      return { success: true };
    } catch (error: any) {
      const msg = error?.message ?? '';
      const isAborted = /abort|signal/i.test(msg);
      return {
        success: false,
        error: isAborted ? 'auth.connectionInterrupted' : (msg || 'Password update failed'),
      };
    }
  }

  /**
   * Check if user has specific role
   */
  hasRole(role: UserRole): boolean {
    return this.currentUserSignal()?.role === role;
  }

  /**
   * Check if user has any of the specified roles
   */
  hasAnyRole(roles: UserRole[]): boolean {
    const role = this.userRole();
    return role ? roles.includes(role) : false;
  }

  /**
   * Get redirect URL based on user role
   */
  getRedirectUrlForRole(role: UserRole): string {
    switch (role) {
      case 'client':
        return '/client/dashboard';
      case 'cleaner':
        // Check if cleaner needs to complete onboarding
        const profile = this.cleanerProfileSignal();
        if (profile?.approvalStatus === 'pending_setup') {
          return '/cleaner/onboarding';
        }
        return '/cleaner/dashboard';
      case 'admin':
        return '/admin/dashboard';
      default:
        return '/login';
    }
  }

  /**
   * Get current session
   */
  getSession(): Observable<Session | null> {
    return this.session$;
  }

  getAuthenticatedEmail(): string | null {
    return this.currentUserSignal()?.email ?? this.sessionSignal()?.user?.email ?? null;
  }

  private async clearAuthSessionSilently(): Promise<void> {
    await this.pushDeviceService.deactivateAllPushDevices();
    this.clearUserData();
    try {
      const { error } = await this.supabase.auth.signOut();
      if (error) {
        console.error('Silent sign-out error before OTP:', error);
      }
    } catch (error) {
      console.error('Silent sign-out failed before OTP:', error);
    }
  }

  /**
   * Refresh user data
   */
  async refreshUserData(): Promise<void> {
    const user = this.currentUserSignal();
    if (user) {
      await this.loadUserData(user.id);
    }
  }

  /**
   * Get dev users (for development user switcher - returns empty in production)
   */
  getDevUsers(): Array<{ email: string; role: UserRole; name: string; status: string }> {
    // Return empty array - this was for mock data only
    return [];
  }

  /**
   * Dev switch user (for development - does nothing in real implementation)
   */
  devSwitchUser(email: string): boolean {
    console.warn('devSwitchUser is not available with real authentication');
    return false;
  }

  /**
   * Update cleaner profile (helper method)
   */
  async updateCleanerProfile(updates: Partial<CleanerProfile>): Promise<{ success: boolean; error?: string }> {
    const userId = this.currentUserSignal()?.id;
    if (!userId) {
      return { success: false, error: 'User not authenticated' };
    }
    
    return new Promise((resolve) => {
      this.cleanerService.updateCleanerProfile(userId, updates).subscribe({
        next: (updatedProfile) => {
          if (updatedProfile) {
            // Update the signal with the new profile data
            this.cleanerProfileSignal.set(updatedProfile);
            // Refresh user data to ensure everything is in sync
            this.refreshUserData().then(() => {
              resolve({ success: true });
            });
          } else {
            resolve({ success: false, error: 'Failed to update profile' });
          }
        },
        error: (err) => {
          console.error('Error updating cleaner profile:', err);
          resolve({ success: false, error: err.message || 'Failed to update profile' });
        }
      });
    });
  }
}
